<template>
    <v-card variant="outlined">
        <v-list-item class="pl-0">
            <template v-slot:prepend>
                <v-img
                    tile
                    class="thumb my-0"
                    :src="$filters.imageUrl(product.info.imageUrl, 200)"
                    :alt="product.info.name"
                    :lazy-src="$filters.srcHost('/placeholder.png')"
                    width="125"
                    @error="imgError = true"
                >
                    <template v-slot:placeholder>
                        <v-row
                            v-show="!imgError && product.info.imageUrl"
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                        >
                            <v-progress-circular indeterminate color="grey-lighten-5" />
                        </v-row>
                    </template>
                </v-img>
            </template>
            <v-row dense no-gutters class="ms-5">
                <v-col cols="9">
                    <v-list-item-title :title="product.info.name" class="wrap-words">
                        <nuxt-link class="text-decoration-none font-weight-medium" :to="productLink" no-prefetch>
                            {{ product.info.name }}
                        </nuxt-link>
                        <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" />
                    </v-list-item-title>
                    <v-list-item-subtitle
                        v-if="product.info.description"
                        class="short-description mt-2"
                        v-html="$filters.stripHtml(product.info.description)"
                    />
                </v-col>
                <v-col v-if="product.price && showPriceDuringSearch" cols="3" class="text-right">
                    <extra-service-card-price-block :offer="minPriceOffer" />
                </v-col>
                <v-col
                    v-if="product.offers[0].price && !minPriceOffer.soldOut && showPriceDuringSearch"
                    cols="3"
                    class="text-right"
                >
                    <strong data-cy="client-price">{{ $t('from') }} {{ $filters.price(minPriceOffer.price) }}</strong>
                    <discount-label :price="minPriceOffer.price" />
                    <small class="mt-2">
                        <commission-amount :commission="minPriceOffer.price.commission" />
                    </small>
                </v-col>
            </v-row>
            <template v-slot:append>
                <v-list-item-action>
                    <v-btn
                        variant="text"
                        :icon="!expanded ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                        @click.stop="toggleOffers"
                    />
                </v-list-item-action>
            </template>
        </v-list-item>
        <v-expand-transition>
            <div v-if="expanded">
                <extra-service-offer-card
                    v-for="(offer, offerIndex) in sortedOffers"
                    :key="offerIndex"
                    :offer="offer"
                    :full-card="true"
                    :data-cy="`extra-service-offer-card-${offerIndex}`"
                >
                    <template v-slot:price>
                        <slot name="price" :offer="offer" />
                    </template>
                </extra-service-offer-card>
                <v-list-item
                    v-if="expanded === expandedLimit && product.offers.length > expandedLimit"
                    density="compact"
                    class="d-flex justify-center"
                    @click.stop="expanded = product.offers.length"
                >
                    <v-icon>mdi-chevron-down</v-icon>
                </v-list-item>
            </div>
        </v-expand-transition>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Setup, Vue} from 'vue-facing-decorator'
    import DiscountLabel from '@/components/snippets/DiscountLabel.vue'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import ExtraServiceOfferCard from '~src/components/extraServices/info/ExtraServiceOfferCard.vue'
    import ExtraServiceCardPriceBlock from '~src/components/extraServices/search/offers/ExtraServiceCardPriceBlock.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import {minPriceOffer} from '~/utils/sort-helpers'

    @Component({
        components: {
            CommissionAmount,
            DiscountLabel,
            ExtraServiceOfferCard,
            ExtraServiceCardPriceBlock,
            SupplierNameLabel,
        },
    })
    export class ExtraServiceCard extends Vue {
        @Prop({default: () => []}) product
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        expanded = 0
        imgError = false

        get sortedOffers() {
            const offers = [...this.product.offers]
            if (this.expandedLimit <= offers.length) {
                this.expanded = offers.length
            }

            return offers.slice(0, this.expanded)
        }

        get expandedLimit() {
            return 3
        }

        get minPriceOffer() {
            return minPriceOffer(this.product)
        }

        get isTO1() {
            return authStore.isTO1
        }

        get productLink() {
            return extraServicesRuntimeStore.infoPageLink(this.product, extraServicesStore.searchRequest)
        }

        toggleOffers() {
            let expanded = 0
            if (!this.expanded) {
                expanded = this.expandedLimit
            }
            this.expanded = expanded
        }
    }

    export default ExtraServiceCard
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .offer-price {
        width: 20%;
    }
    @media #{map.get(settings.$display-breakpoints, 'xs')} {
        .offer-price {
            width: 25%;
        }
    }

    .wrap-words {
        white-space: normal;
    }

    .short-description {
        -webkit-line-clamp: 4;
    }
</style>
